/**
 * フローティングチャットボタンコンポーネント
 * 画面右下に固定表示され、クリックでチャットボットモーダルを開く
 */

import React, { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Bot, MessageCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/shared/lib/utils';
import { useAdminOnly } from '@/shared/hooks/useAdminOnly';

interface FloatingChatbotButtonProps {
  onOpenChat: () => void;
  className?: string;
  disabled?: boolean;
}

export function FloatingChatbotButton({
  onOpenChat,
  className,
  disabled = false,
}: FloatingChatbotButtonProps) {
  // Admin権限チェック（開発環境では無効化）
  const { shouldShowAdminFeature } = useAdminOnly({ disableAdminCheck: true });
  const [isHovered, setIsHovered] = useState(false);

  // Admin権限がない場合はボタンを非表示
  if (!shouldShowAdminFeature) {
    return null;
  }

  return (
    <motion.div
      className={cn(
        "fixed bottom-6 right-6 z-50",
        className
      )}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        delay: 0.2
      }}
    >
      <div className="relative">
        {/* メインボタン */}
        <Button
          onClick={onOpenChat}
          disabled={disabled}
          size="lg"
          data-testid="floating-chatbot-button"
          className={cn(
            "h-14 w-14 rounded-full shadow-lg transition-all duration-300 ease-in-out",
            "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800",
            "border-2 border-blue-500/20 hover:border-blue-400/30",
            "group relative overflow-hidden",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* グラデーション背景アニメーション */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* アイコン */}
          <motion.div
            animate={{
              rotate: isHovered ? 12 : 0,
              scale: isHovered ? 1.1 : 1
            }}
            transition={{ duration: 0.2 }}
          >
            <Bot className="h-6 w-6 text-white relative z-10" />
          </motion.div>

          {/* ホバー時の波紋エフェクト */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                className="absolute inset-0 bg-white/10 rounded-full"
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
              />
            )}
          </AnimatePresence>
        </Button>

        {/* 通知ドット（必要時に表示） */}
        <motion.div
          className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full border-2 border-white"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            className="absolute inset-0 bg-red-400 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>

        {/* ツールチップ */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              className="absolute bottom-full right-0 mb-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              AI アシスタント
              {/* 矢印 */}
              <div className="absolute top-full right-4 -translate-x-1/2">
                <div className="border-4 border-transparent border-t-gray-900" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default FloatingChatbotButton;